import React from 'react';
import {
  Users, DollarSign, FileText, TrendingUp, Calendar, Filter, Download, RefreshCw, CheckCircle, XCircle, Clock, Search, Mail, Phone, Building, CreditCard, Percent, AlertTriangle, Check, X, Edit2, Save, Wallet, Send, ArrowRight, Banknote, Bell, Trash2, Eye
} from 'lucide-react';
import { useSuperAdminContext } from '../SuperAdminContext';

export default function AgentsTab() {
  const ctx = useSuperAdminContext();
  const { activeTab, agentBalances, agentPayoutAmount, agentPayoutNotes, agentPayoutReference, agentSummary, agents, handleAgentPayout, handleApproveCommissions, openAgentPayoutModal, selectedAgent, setAgentPayoutAmount, setAgentPayoutNotes, setAgentPayoutReference, setShowAgentPayoutModal, showAgentPayoutModal } = ctx;

  return (
    <>
            {activeTab === 'agents' && (
              <div className="agents-content" data-testid="agents-tab">
                <div className="section-header">
                  <h2><Users size={24} /> Agent Management & Commissions</h2>
                </div>
                
                {/* Commission Summary Cards */}
                {agentSummary && (
                  <div className="payout-summary-cards">
                    <div className="summary-card">
                      <div className="summary-icon agents"><Users size={24} /></div>
                      <div className="summary-content">
                        <span className="summary-label">Total Agents</span>
                        <span className="summary-value">{agentSummary.total_agents}</span>
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-icon pending"><Clock size={24} /></div>
                      <div className="summary-content">
                        <span className="summary-label">Pending Commissions</span>
                        <span className="summary-value">${agentSummary.total_pending_balance?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-icon approved"><CheckCircle size={24} /></div>
                      <div className="summary-content">
                        <span className="summary-label">Approved (Ready to Pay)</span>
                        <span className="summary-value">${agentSummary.total_approved_balance?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-icon paid"><Send size={24} /></div>
                      <div className="summary-content">
                        <span className="summary-label">Total Paid Out</span>
                        <span className="summary-value">${agentSummary.total_paid_out?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Agent Balances Table */}
                <div className="table-section">
                  <h3>Agent Commission Balances</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Agent</th>
                          <th>Email</th>
                          <th>Revenue Share</th>
                          <th>Referred</th>
                          <th>Pending</th>
                          <th>Approved</th>
                          <th>Total Paid</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentBalances.length === 0 ? (
                          <tr>
                            <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                              No agents found. Create agents via the Admin Portal or registration.
                            </td>
                          </tr>
                        ) : (
                          agentBalances.map((agent) => (
                            <tr key={agent.agent_id}>
                              <td><strong>{agent.agent_name}</strong></td>
                              <td>{agent.agent_email}</td>
                              <td>{agent.revenue_share_percentage}%</td>
                              <td>{agent.referred_businesses}</td>
                              <td className={agent.pending_balance > 0 ? 'text-warning' : ''}>
                                ${agent.pending_balance?.toFixed(2)}
                                {agent.pending_commission_count > 0 && (
                                  <span className="commission-count">({agent.pending_commission_count})</span>
                                )}
                              </td>
                              <td className={agent.approved_balance > 0 ? 'text-success' : ''}>
                                ${agent.approved_balance?.toFixed(2)}
                              </td>
                              <td>${agent.total_paid?.toFixed(2)}</td>
                              <td className="actions-cell">
                                {agent.pending_balance > 0 && (
                                  <button 
                                    className="btn-small approve"
                                    onClick={() => handleApproveCommissions(agent.agent_id)}
                                    title="Approve pending commissions"
                                    data-testid={`approve-btn-${agent.agent_id}`}
                                  >
                                    <Check size={14} /> Approve
                                  </button>
                                )}
                                {agent.approved_balance > 0 && (
                                  <button 
                                    className="btn-small payout"
                                    onClick={() => openAgentPayoutModal(agent)}
                                    title="Pay out approved commissions"
                                    data-testid={`payout-btn-${agent.agent_id}`}
                                  >
                                    <Send size={14} /> Pay Out
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="agent-info-box">
                  <h4>About Agent Commissions</h4>
                  <p>Agents earn commissions when payments are completed by their referred merchants.</p>
                  <ul>
                    <li><strong>Pending:</strong> Commissions waiting to be approved</li>
                    <li><strong>Approved:</strong> Commissions approved and ready for payout</li>
                    <li><strong>Paid:</strong> Commissions that have been paid out</li>
                  </ul>
                  <p>Workflow: <em>Payment Completed → Commission Created (Pending) → Approve → Pay Out</em></p>
                </div>
              </div>
            )}

            {/* Agent Payout Modal */}
            {showAgentPayoutModal && selectedAgent && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>Create Agent Commission Payout</h3>
                    <button className="modal-close" onClick={() => setShowAgentPayoutModal(false)}>
                      <X size={20} />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="payout-summary">
                      <p><strong>Agent:</strong> {selectedAgent.agent_name}</p>
                      <p><strong>Email:</strong> {selectedAgent.agent_email}</p>
                      <p><strong>Approved Balance:</strong> <span className="text-success">${selectedAgent.approved_balance?.toFixed(2)}</span></p>
                    </div>
                    <div className="form-group">
                      <label>Payout Amount (USD)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={agentPayoutAmount}
                        onChange={(e) => setAgentPayoutAmount(e.target.value)}
                        placeholder={`Max: $${selectedAgent.approved_balance?.toFixed(2)}`}
                        max={selectedAgent.approved_balance}
                      />
                    </div>
                    <div className="form-group">
                      <label>Payment Reference (Optional)</label>
                      <input
                        type="text"
                        value={agentPayoutReference}
                        onChange={(e) => setAgentPayoutReference(e.target.value)}
                        placeholder="e.g., ACH #123456, Check #789"
                      />
                    </div>
                    <div className="form-group">
                      <label>Notes (Optional)</label>
                      <textarea
                        value={agentPayoutNotes}
                        onChange={(e) => setAgentPayoutNotes(e.target.value)}
                        placeholder="Any additional notes..."
                        rows={3}
                      />
                    </div>
                    <div className="modal-actions">
                      <button className="btn-secondary" onClick={() => setShowAgentPayoutModal(false)}>
                        Cancel
                      </button>
                      <button 
                        className="btn-primary"
                        onClick={handleAgentPayout}
                        disabled={!agentPayoutAmount || parseFloat(agentPayoutAmount) <= 0 || parseFloat(agentPayoutAmount) > selectedAgent.approved_balance}
                      >
                        <Send size={16} /> Process Payout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
    </>
  );
}
